import { Card, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Formik } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import { COMPANY_PERMISSIONS } from "@/constants/modules.permissions";
import SWR_KEYS from "@/constants/swr-keys";
import useToast from "@/contexts/toast-context";
import useGetAllCustomerConfigurations from "@/hooks/requests/customer-configuration/use-get-all-customer-configurations";
import useUpdateCustomerConfiguration from "@/hooks/requests/customer-configuration/use-update-customer-configuration";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useMutate from "@/hooks/use-mutate";
import usePagination from "@/hooks/use-pagination";
import { CustomerFormMessages } from "@/messages/customer/customer-form.messages";

import ConsentLGPDCard from "./components/ConsentimentoLGPDCard";
import ClosedDaysCard from "./components/ClosedDaysCard";
import DaysUntilDueCard from "./components/DaysUntilDueCard";
import FreightCard from "./components/FreightCard";
import ServiceHoursCard from "./components/ServiceHoursCard";
import customerConfigurationService from "@/services/customer-configuration.service";
import { buildFormSchema, buildInitialData, normalizeCustomerConfigurationFormData } from "./form-data";
import { CustomerConfigurationFormData } from "../../types";

type Props = {
  messages: CustomerFormMessages;
};

function ConfigurationTab({ messages }: Props) {
  const hasNoPermissionToEdit = useHasNoPermission(COMPANY_PERMISSIONS.EDIT);
  const { mutate } = useMutate();
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;
  const { update, isLoading } = useUpdateCustomerConfiguration();
  const getAllCustomerConfigurations = useGetAllCustomerConfigurations({ pageNumber, pageSize });
  const {
    data: customerConfig,
    error: customerConfigError,
    isLoading: isLoadingConfig,
  } = getAllCustomerConfigurations;
  const { showToast } = useToast();

  const handleUpdate = async (
    formData: CustomerConfigurationFormData,
    resetForm: (nextState?: { values: CustomerConfigurationFormData }) => void
  ) => {
    if (customerConfig && !customerConfig.items[0]) {
      return showToast({ text: messages.noConfigurationFoundToast, type: "error" });
    }

    const successMessage = messages.updateCustomerConfigurationSuccess;
    const errorMessage = messages.updateCustomeConfigurationrError;

    const _id = Number(customerConfig?.items[0]?.id) || 0;
    const normalizedFormData = normalizeCustomerConfigurationFormData(formData);

    await update({ _id, formData: normalizedFormData, successMessage, errorMessage });

    await mutate(SWR_KEYS.getAllCustomerConfigurations);

    const refreshedConfig = await customerConfigurationService.getById(_id);
    resetForm({
      values: buildInitialData(refreshedConfig) as CustomerConfigurationFormData,
    });
  };

  return (
    <Suspense isSuspended={isLoadingConfig && !customerConfig?.items[0]} fallback={<CircularProgress />}>
      {customerConfig?.items[0] ? (
        <Formik
          initialValues={buildInitialData(customerConfig.items[0])}
          validationSchema={buildFormSchema(messages)}
          validateOnChange={false}
          onSubmit={(formData, { resetForm }) => handleUpdate(formData, resetForm)}
        >
          {({ submitForm }) => (
            <MDBox mt={4}>
              <Grid container spacing={3} alignItems="stretch">
                <Grid size={{ xs: 12, md: 6.5 }}>
                  <MDBox height="100%">
                    <FreightCard messages={messages} />
                  </MDBox>
                </Grid>

                <Grid size={{ xs: 12, md: 5.5 }}>
                  <MDBox height="100%">
                    <DaysUntilDueCard messages={messages} />
                  </MDBox>
                </Grid>

                <Grid size={{ xs: 12, lg: 12 }}>
                  <MDBox height="100%">
                    <ConsentLGPDCard messages={messages} />
                  </MDBox>
                </Grid>

                <Grid size={{ xs: 12, lg: 12 }}>
                  <MDBox height="100%">
                    <ServiceHoursCard messages={messages} />
                  </MDBox>
                </Grid>

                <Grid size={{ xs: 12, lg: 12 }}>
                  <MDBox height="100%">
                    <ClosedDaysCard messages={messages} />
                  </MDBox>
                </Grid>
              </Grid>

              <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={submitForm}
                  isLoading={isLoading}
                  disabled={hasNoPermissionToEdit}
                >
                  {messages.updateButton}
                </MDButton>
              </MDBox>

              {customerConfigError && (
                <Card>
                  <AlertBlock
                    title={messages.loadConfigurationErrorTitle}
                    description={messages.loadConfigurationErrorDescription}
                    alt={messages.noConfigurationFoundTitle}
                  />
                </Card>
              )}
            </MDBox>
          )}
        </Formik>
      ) : (
        <Card sx={{ p: 2, mt: 2 }}>
          <AlertBlock
            title={messages.noConfigurationFoundTitle}
            description={messages.noConfigurationFoundDescription}
            alt={messages.noConfigurationFoundTitle}
          />
        </Card>
      )}
    </Suspense>
  );
}

export default ConfigurationTab;
